// src/pages/Level-2.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import Confetti from "react-confetti";

import { STREAM_VIDEOS, CF_STREAM_CUSTOMER } from "../videos/streamVideos";
import HlsVideoPlayer from "../components/HlsVideoPlayer";
import { TrophyStorage } from "../services/trophyStorage";

/* ─────────────────────────────────────────────
   CONFIG – Level 2: Happy Birthday (Zeb)
   ───────────────────────────────────────────── */

const LEVEL2_INTRO_STREAM_ID = STREAM_VIDEOS.L2_ZEB_INTRO.id;
const LEVEL2_BLACK_STREAM_ID = STREAM_VIDEOS.L2_BLACK_KEYS.id;

// ✅ LevelTemplate supports: videoSrc="stream:<id>"
const LEVEL2_VIDEO_SRC = `stream:${LEVEL2_INTRO_STREAM_ID}`;

// ✅ HLS manifests (for “ended” + tap-to-start unmuted)
const LEVEL2_INTRO_MANIFEST = `https://${CF_STREAM_CUSTOMER}/${LEVEL2_INTRO_STREAM_ID}/manifest/video.m3u8`;
const LEVEL2_BLACK_MANIFEST = `https://${CF_STREAM_CUSTOMER}/${LEVEL2_BLACK_STREAM_ID}/manifest/video.m3u8`;

const LEVEL2_MELODY_LINES = [
  { id: 0, notes: ["C", "C", "D", "C", "F", "E"], lyric: "Happy Birthday To You" },
  { id: 1, notes: ["C", "C", "D", "C", "G", "F"], lyric: "Happy Birthday To You" },
  { id: 2, notes: ["C", "C", "C5", "A", "F", "E", "D"], lyric: "Happy Birthday Dear Zebra" },
  { id: 3, notes: ["A#", "A#", "A", "F", "G", "F"], lyric: "Happy Birthday To You" },
];

const LEVEL2_LYRIC_GROUPS = [
  ["Happy", "Birthday", "To You"],
  ["Happy", "Birthday", "To You"],
  ["Happy", "Birthday", "Dear", "Zebra"],
  ["Happy", "Birthday", "To You"],
];

const LEVEL2_NOTE_GROUP_INDEXES = [
  [
    { start: 0, end: 1 },
    { start: 2, end: 3 },
    { start: 4, end: 5 },
  ],
  [
    { start: 0, end: 1 },
    { start: 2, end: 3 },
    { start: 4, end: 5 },
  ],
  [
    { start: 0, end: 1 },
    { start: 2, end: 3 },
    { start: 4, end: 4 },
    { start: 5, end: 6 },
  ],
  [
    { start: 0, end: 1 },
    { start: 2, end: 3 },
    { start: 4, end: 5 },
  ],
];

/* ─────────────────────────────────────────────
   HERO COMPONENT WITH PARTY ANIMATIONS
   ───────────────────────────────────────────── */

function ZebBirthdayHero({ noteEvent, isComplete }) {
  const [shake, setShake] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [applause, setApplause] = useState(false);
  const [hitEmojis, setHitEmojis] = useState([]);

  useEffect(() => {
    if (noteEvent === "miss") {
      setShake(true);
      const t = setTimeout(() => setShake(false), 300);
      return () => clearTimeout(t);
    }
  }, [noteEvent]);

  useEffect(() => {
    if (noteEvent === "hit") {
      const cake = document.querySelector("#zeb-birthday-cake");
      if (cake) {
        cake.classList.add("twinkle");
        const t = setTimeout(() => cake.classList.remove("twinkle"), 400);
        return () => clearTimeout(t);
      }

      if (Math.random() < 0.45) {
        const EMOJIS = ["🎂", "🕯️", "🎉", "👏"];
        const emoji = EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
        const id = Date.now() + Math.random();

        setHitEmojis((prev) => [
          ...prev,
          { id, emoji, x: 20 + Math.random() * 60, y: 25 + Math.random() * 35 },
        ]);

        setTimeout(() => {
          setHitEmojis((prev) => prev.filter((e) => e.id !== id));
        }, 900);
      }
    }
  }, [noteEvent]);

  useEffect(() => {
    if (isComplete) {
      setShowConfetti(true);
      setApplause(true);
      const t = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(t);
    }
  }, [isComplete]);

  return (
    <div className="relative w-full max-w-4xl mx-auto mt-2">
      {showConfetti && typeof window !== "undefined" && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={220}
          recycle={false}
          gravity={0.15}
        />
      )}

      {applause && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-5xl animate-bounce">👏👏👏</div>
        </div>
      )}

      <div className="relative">
        <div
          className={`transition-transform duration-200 ${
            shake ? "scale-[0.97] -translate-y-[2px]" : ""
          }`}
        >
          <img
            id="zeb-birthday-cake"
            src="/images/Zeb-Birthday.png"
            alt="Zeb Birthday Party"
            className="w-full h-[150px] md:h-[190px] object-contain rounded-[20px] shadow-md bg-[#FFEFE4]"
          />
        </div>

        <div className="pointer-events-none absolute inset-0">
          {hitEmojis.map((e) => (
            <div
              key={e.id}
              className="absolute text-2xl sm:text-3xl animate-bounce"
              style={{
                left: `${e.x}%`,
                top: `${e.y}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              {e.emoji}
            </div>
          ))}
        </div>
      </div>

      <style>{`.twinkle { filter: drop-shadow(0 0 10px rgba(236,72,153,0.55)); }`}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────
   HLS OVERLAY (tap-to-start, unmuted, auto-close on end)
   ───────────────────────────────────────────── */

function TapToStartHlsOverlay({ open, title, manifestSrc, onDone, subtitle }) {
  const [started, setStarted] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (open) {
      setStarted(false);
      setFading(false);
    }
  }, [open]);

  if (!open) return null;

  const finish = () => {
    setFading(true);
    setTimeout(() => onDone?.(), 520);
  };

  return (
    <div
      className={`fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 p-3 transition-opacity duration-500 ${
        fading ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl bg-black">
        <div className="relative w-full aspect-video bg-slate-900">
          {!started ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-4">
              <div className="text-white text-xl sm:text-2xl font-extrabold">
                {title}
              </div>
              {subtitle && (
                <div className="text-white/80 text-sm sm:text-base max-w-xl">
                  {subtitle}
                </div>
              )}
              <button
                onClick={() => setStarted(true)}
                className="px-6 py-3 rounded-full bg-white text-slate-900 font-extrabold shadow border"
              >
                ▶ Tap to Start
              </button>
            </div>
          ) : (
            <HlsVideoPlayer
              manifestSrc={manifestSrc}
              autoPlay={true}
              controls={true}
              className="w-full h-full object-cover"
              onEnded={finish}
            />
          )}

          <div className="absolute top-2 right-2">
            <button
              onClick={finish}
              className="px-3 py-1 rounded-full bg-white/90 backdrop-blur text-sm font-bold border"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   BLACK KEYS MODAL (tap-to-start, auto-close on end)
   ───────────────────────────────────────────── */

function LearnBlackKeysModal({ open, manifestSrc, onClose }) {
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (open) setStarted(false);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-3">
      <div className="w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl bg-white">
        <div className="relative">
          <div className="w-full aspect-video bg-slate-900 relative">
            {!started ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-4">
                <div className="text-white text-xl sm:text-2xl font-extrabold">
                  Learn the Black Keys 🎹
                </div>
                <div className="text-white/80 text-sm sm:text-base max-w-xl"></div>
                <button
                  onClick={() => setStarted(true)}
                  className="px-6 py-3 rounded-full bg-white text-slate-900 font-extrabold shadow border"
                >
                  ▶ Tap to Start
                </button>
              </div>
            ) : (
              <HlsVideoPlayer
                manifestSrc={manifestSrc}
                autoPlay={true}
                controls={true}
                className="w-full h-full object-cover"
                onEnded={onClose}
              />
            )}
          </div>

          <div className="absolute top-2 right-2 flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1 rounded-full bg-white/90 backdrop-blur text-sm font-bold border"
            >
              Skip to Game
            </button>
          </div>

          <div className="p-4 sm:p-5 bg-white">
            <div className="text-lg sm:text-xl font-extrabold text-slate-900">
              Learn the Black Keys (Sharps)
            </div>
            <div className="text-sm sm:text-base text-slate-600 mt-1">
              Watch this quick video, then we’ll play{" "}
              <span className="font-bold">Happy Birthday</span> — look out for{" "}
              <span className="font-bold">A#</span> 🎹✨
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────── */

export default function Level2() {
  const navigate = useNavigate();

  const INTRO_SEEN_KEY = "tt_l2_intro_seen";
  const BLACK_SEEN_KEY = "tt_l2_blackkeys_seen";

  const [showIntroOverlay, setShowIntroOverlay] = useState(false);
  const [showBlackKeys, setShowBlackKeys] = useState(false);

  useEffect(() => {
    const introSeen =
      typeof window !== "undefined" &&
      localStorage.getItem(INTRO_SEEN_KEY) === "1";
    if (!introSeen) setShowIntroOverlay(true);
  }, []);

  const onIntroDone = () => {
    setShowIntroOverlay(false);
    try {
      localStorage.setItem(INTRO_SEEN_KEY, "1");
    } catch {}
  };

  // ✅ Called ONLY when user clicks Start Game inside LevelTemplate
  const onStartGame = () => {
    const blackSeen =
      typeof window !== "undefined" &&
      localStorage.getItem(BLACK_SEEN_KEY) === "1";
    if (!blackSeen) setTimeout(() => setShowBlackKeys(true), 150);
  };

  const onBlackKeysClose = () => {
    setShowBlackKeys(false);
    try {
      localStorage.setItem(BLACK_SEEN_KEY, "1");
    } catch {}
  };

  return (
    <>
      <LevelTemplate
        levelId={2}
        currentLevelForSidebar={2}
        videoSrc={LEVEL2_VIDEO_SRC}
        onStartGame={onStartGame}
        melodyLines={LEVEL2_MELODY_LINES}
        lyricGroupsPerLine={LEVEL2_LYRIC_GROUPS}
        noteGroupIndexesPerLine={LEVEL2_NOTE_GROUP_INDEXES}
        backgroundImage="/images/Zeb-Birthday.png"
        heroImageFit="cover"
        renderHero={(heroProps) => <ZebBirthdayHero {...heroProps} />}
        completionTitle="Happy Birthday, Zeb!"
        completionBody={
          <>
            You played the full{" "}
            <span className="font-semibold text-[#EC4899]">Happy Birthday</span>{" "}
            melody — the party was a success, and everyone cheered for Zeb!
          </>
        }
        onComplete={() => {
          TrophyStorage.markLevelAndStar(2);
        }}
        // ✅ FIX: route to the V1.1 level-3 (not old chapter-3)
        onNextLevel={() => navigate("/level-3")}
      />

      <TapToStartHlsOverlay
        open={showIntroOverlay}
        title="Zeb’s Birthday 🎂"
        manifestSrc={LEVEL2_INTRO_MANIFEST}
        onDone={onIntroDone}
      />

      <LearnBlackKeysModal
        open={showBlackKeys}
        manifestSrc={LEVEL2_BLACK_MANIFEST}
        onClose={onBlackKeysClose}
      />
    </>
  );
}